import type { TimestampField } from "@prismicio/client";
import { asDate } from "@prismicio/helpers";
import { useEffect, useMemo, useState } from "react";

export type CountdownKey = "days" | "hours" | "minutes" | "seconds";

export type TimeLeft = Record<CountdownKey, number> & { completed: boolean };

export type CountdownPart = {
  key: CountdownKey;
  value: string;
  label: string;
};

export const DEFAULT_LABELS: Record<CountdownKey, string> = {
  days: "Days",
  hours: "Hours",
  minutes: "Minutes",
  seconds: "Seconds",
};

const getNlOffset = (probe: Date): string => {
  const part = new Intl.DateTimeFormat("en", {
    timeZone: "Europe/Amsterdam",
    timeZoneName: "shortOffset",
  })
    .formatToParts(probe)
    .find((p) => p.type === "timeZoneName")?.value;

  // part is e.g. "GMT+2" or "GMT+1" — extract sign and hours
  const match = part?.match(/GMT([+-])(\d+)/);
  if (!match) return "+01:00";
  const [, sign, h] = match;
  return `${sign}${h.padStart(2, "0")}:00`;
};

const getTargetDate = (targetDate: string | null | undefined) => {
  if (!targetDate) return null;

  // Timestamp fields carry their own time and offset, so end exactly at that moment.
  if (targetDate.includes("T")) {
    const date = asDate(targetDate as TimestampField<"filled">);
    return date && !isNaN(date.getTime()) ? date : null;
  }

  // Use a midday probe to determine CET/CEST offset for the target date,
  // so the countdown ends at 23:59:59 Dutch time regardless of viewer timezone.
  const probe = new Date(`${targetDate}T12:00:00Z`);
  if (isNaN(probe.getTime())) return null;

  const offset = getNlOffset(probe);
  const date = new Date(`${targetDate}T23:59:59${offset}`);
  return isNaN(date.getTime()) ? null : date;
};

const getTimeLeft = (targetDate: Date | null): TimeLeft => {
  if (!targetDate) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0, completed: true };
  }

  const distance = targetDate.getTime() - Date.now();

  if (distance <= 0) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0, completed: true };
  }

  return {
    days: Math.floor(distance / (1000 * 60 * 60 * 24)),
    hours: Math.floor((distance / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((distance / (1000 * 60)) % 60),
    seconds: Math.floor((distance / 1000) % 60),
    completed: false,
  };
};

export function useCountdown(targetDateStr: string | null | undefined) {
  const targetDate = useMemo(
    () => getTargetDate(targetDateStr),
    [targetDateStr],
  );
  const [timeLeft, setTimeLeft] = useState<TimeLeft>(() =>
    getTimeLeft(targetDate),
  );

  useEffect(() => {
    setTimeLeft(getTimeLeft(targetDate));

    if (!targetDate) return;

    const interval = window.setInterval(() => {
      setTimeLeft(getTimeLeft(targetDate));
    }, 1000);

    return () => window.clearInterval(interval);
  }, [targetDate]);

  return timeLeft;
}

type LabelFields = {
  days_label?: string | null;
  hours_label?: string | null;
  minutes_label?: string | null;
  seconds_label?: string | null;
};

export function resolveLabels(primary: LabelFields) {
  return {
    days: primary.days_label || DEFAULT_LABELS.days,
    hours: primary.hours_label || DEFAULT_LABELS.hours,
    minutes: primary.minutes_label || DEFAULT_LABELS.minutes,
    seconds: primary.seconds_label || DEFAULT_LABELS.seconds,
  };
}

export function buildParts(
  timeLeft: TimeLeft,
  labels: Record<CountdownKey, string>,
): CountdownPart[] {
  return [
    { key: "days", value: String(timeLeft.days).padStart(2, "0"), label: labels.days },
    { key: "hours", value: String(timeLeft.hours).padStart(2, "0"), label: labels.hours },
    { key: "minutes", value: String(timeLeft.minutes).padStart(2, "0"), label: labels.minutes },
    { key: "seconds", value: String(timeLeft.seconds).padStart(2, "0"), label: labels.seconds },
  ];
}
